/**
 * AdLevelsTable — таблица «Реклама по уровням»
 * ==============================================
 *
 * Строка «Общее» всегда первая и в сортировке не участвует; колонка
 * уровня и шапка липкие — плейсментов десятки, таблица широкая и длинная.
 * Градиент считается по каждому столбцу отдельно (свой мин/макс), строка
 * «Общее» в шкалу не входит, иначе она одна «выжигала» бы весь столбец.
 */

import React, { useMemo } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronRight } from 'lucide-react';
import {
  LEVEL_SORT_KEY,
  METRIC_COLUMNS,
  cellValue,
  columnRanges,
  formatCell,
  gradientIntensity,
  sortRows
} from './model';

const MAX_ALPHA = 0.55;

function SortIcon({ active, dir }) {
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return <Icon className="h-3 w-3 shrink-0 opacity-70" />;
}

function headerClass(column) {
  const base = 'px-2 py-2 text-right font-medium whitespace-nowrap align-bottom bg-muted text-xs';
  if (column.kind === 'detail') return `${base} text-muted-foreground font-normal`;
  if (column.kind === 'group') return `${base} border-l border-border`;
  return base;
}

function HeaderCell({ column, sortKey, sortDir, onSort, onToggleGroup }) {
  const active = sortKey === column.id;
  return (
    <th className={headerClass(column)} title={column.hint}>
      <div className="flex items-center justify-end gap-1">
        {column.kind === 'group' && column.expandable && (
          <button
            type="button"
            onClick={() => onToggleGroup(column.groupId)}
            className="text-muted-foreground hover:text-foreground"
            title={column.expanded ? 'Свернуть группу' : 'Развернуть группу'}
          >
            {column.expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        )}
        <button
          type="button"
          onClick={() => onSort(column.id)}
          className="flex items-center gap-1 hover:text-foreground"
        >
          {column.kind === 'detail' ? `↳ ${column.label}` : column.label}
          <SortIcon active={active} dir={sortDir} />
        </button>
      </div>
    </th>
  );
}

function cellClass(column, isTotalRow) {
  const parts = ['px-2 py-1 text-right tabular-nums whitespace-nowrap'];
  if (column.kind === 'group') parts.push('border-l border-border font-medium');
  if (column.kind === 'detail') parts.push('text-muted-foreground');
  if (isTotalRow) parts.push('font-semibold');
  return parts.join(' ');
}

function Row({ row, columns, mode, ranges, gradient, isTotalRow }) {
  return (
    <tr className={isTotalRow ? 'bg-muted/60 border-b-2 border-border' : 'border-b border-border/60 hover:bg-muted/30'}>
      <td
        className={`px-2 py-1 sticky left-0 z-10 whitespace-nowrap font-medium ${
          isTotalRow ? 'bg-muted' : 'bg-background'
        }`}
      >
        {isTotalRow ? 'Общее' : row.level}
      </td>
      {columns.map((column) => {
        const value = cellValue(row, column, mode);
        const alpha = gradient && !isTotalRow ? gradientIntensity(value, ranges.get(column.id)) * MAX_ALPHA : 0;
        return (
          <td
            key={column.id}
            className={cellClass(column, isTotalRow)}
            style={alpha > 0 ? { backgroundColor: `rgba(59, 130, 246, ${alpha.toFixed(3)})` } : undefined}
          >
            {formatCell(value, column, mode)}
          </td>
        );
      })}
    </tr>
  );
}

export function AdLevelsTable({
  data,
  placementColumns,
  mode,
  gradient,
  sortKey,
  sortDir,
  onSort,
  onToggleGroup
}) {
  const columns = useMemo(() => [...METRIC_COLUMNS, ...placementColumns], [placementColumns]);
  const rows = useMemo(
    () => sortRows(data?.rows, sortKey, sortDir, columns, mode),
    [data?.rows, sortKey, sortDir, columns, mode]
  );
  const ranges = useMemo(() => columnRanges(data?.rows, columns, mode), [data?.rows, columns, mode]);

  if (!data?.rows?.length) {
    return <div className="text-center py-8 text-sm text-muted-foreground">Нет данных за выбранную когорту</div>;
  }

  return (
    <div className="overflow-auto max-h-[70vh] border rounded-md">
      <table className="text-sm border-separate border-spacing-0">
        <thead className="sticky top-0 z-20">
          <tr>
            <th className="px-2 py-2 text-left font-medium whitespace-nowrap align-bottom bg-muted text-xs sticky left-0 z-30">
              <button
                type="button"
                onClick={() => onSort(LEVEL_SORT_KEY)}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Уровень
                <SortIcon active={sortKey === LEVEL_SORT_KEY} dir={sortDir} />
              </button>
            </th>
            {columns.map((column) => (
              <HeaderCell
                key={column.id}
                column={column}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
                onToggleGroup={onToggleGroup}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          <Row row={data.total} columns={columns} mode={mode} ranges={ranges} gradient={gradient} isTotalRow />
          {rows.map((row) => (
            <Row key={row.level} row={row} columns={columns} mode={mode} ranges={ranges} gradient={gradient} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdLevelsTable;
